// ═══════════════════════════════════════════════════════════════════════════════
// Complete pending pair invite after login / register
// ═══════════════════════════════════════════════════════════════════════════════

import type { NavigateFunction } from 'react-router-dom';
import type { UserProfile, UserPublic } from '@fdh/shared';
import { USER_ROLE } from '@fdh/shared';
import { api } from './api';
import {
  clearPendingPairInvite,
  isHelperAlreadyPairedJoinError,
  isPairInviteJoinExpiredError,
  readPendingPairInvite,
} from './pairInviteSession';

type JoinPairingResponse = {
  pairingId: string;
  employer: UserPublic;
};

const isPairedProfile = (user: UserProfile): boolean =>
  Boolean(user.pairedAt && user.pairedAt.trim() !== '');

export const executePairInviteJoin = async (
  refreshUser: () => Promise<UserProfile | null>,
  navigate: NavigateFunction
): Promise<boolean> => {
  const pending = readPendingPairInvite();
  if (!pending?.code) {
    return false;
  }

  try {
    const response = await api.post<JoinPairingResponse>('/pairing/join', {
      code: pending.code,
    });
    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Pairing failed');
    }
    await refreshUser();
    clearPendingPairInvite();
    navigate('/pairing/success', {
      replace: true,
      state: {
        pairingId: response.data.pairingId,
        employer: response.data.employer,
      },
    });
    return true;
  } catch (err) {
    if (isHelperAlreadyPairedJoinError(err)) {
      clearPendingPairInvite();
      navigate('/pairing/status', { replace: true });
      return false;
    }
    if (isPairInviteJoinExpiredError(err)) {
      clearPendingPairInvite();
      navigate('/pair-invite/expired', { replace: true });
      return false;
    }
    throw err;
  }
};

export type PairInviteAfterAuthResult = 'joined' | 'skipped' | 'failed' | 'wrong_role';

export const tryCompletePairInviteAfterAuth = async (
  user: UserProfile,
  refreshUser: () => Promise<UserProfile | null>,
  navigate: NavigateFunction
): Promise<PairInviteAfterAuthResult> => {
  const pending = readPendingPairInvite();
  if (!pending?.code) {
    return 'skipped';
  }

  if (user.role !== USER_ROLE.HELPER) {
    clearPendingPairInvite();
    return 'wrong_role';
  }

  if (isPairedProfile(user)) {
    clearPendingPairInvite();
    return 'skipped';
  }

  const joined = await executePairInviteJoin(refreshUser, navigate);
  return joined ? 'joined' : 'failed';
};

export const navigateToPairInviteExpired = (navigate: NavigateFunction): void => {
  clearPendingPairInvite();
  navigate('/pair-invite/expired', { replace: true });
};
